/**
 * ARMY RIBBON BUILDER V2
 * ----------------------
 * Rack: 1 product per size, variants for style (Tight/Spaced/Centered/Offset).
 * Devices: quantity dropdown adds single device × N to cart and preview.
 * Ribbons: 400×100. Devices: 200×200 square transparent PNGs.
 * Max 4 device slots per ribbon. Wide knot/clasp devices reserve all 4 slots.
 */
(function () {
  'use strict';

  let rackData, ribbonData, config;
  try { rackData = JSON.parse(document.getElementById('arb2-rack-data').textContent); } catch (e) { console.error('ARB2: rack parse', e); rackData = []; }
  try { ribbonData = JSON.parse(document.getElementById('arb2-ribbon-data').textContent); } catch (e) { console.error('ARB2: ribbon parse', e); ribbonData = []; }
  try { config = JSON.parse(document.getElementById('arb2-config').textContent); } catch (e) { console.error('ARB2: config parse', e); config = { assembly_fee_tiers: [], assembly_tier1_max: 6, assembly_tier2_max: 13, device_fee_variant_id: null, device_fee_price: 0 }; }

  function inferRackSize(p) { if (p.rack_size > 0) return p.rack_size; const t = p.title.toLowerCase(); for (const [k, v] of Object.entries({ 'double': 2, 'triple': 3, 'quad': 4 })) if (t.includes(k)) return v; const m = t.match(/(\d+)[\s\-]*ribbon/i) || t.match(/^(\d+)[\s\-]/); return m ? parseInt(m[1], 10) : 0; }
  rackData.forEach(r => { r.rack_size = inferRackSize(r); });

  const ribbonsSorted = [...ribbonData].sort((a, b) => a.precedence - b.precedence);
  // One product per size — variants for style options
  const racksBySize = {};
  rackData.forEach(r => { if (r.rack_size > 0) racksBySize[r.rack_size] = r; });
  const availableSizes = Object.keys(racksBySize).map(Number).sort((a, b) => a - b);

  const PER_PAGE = 16, MAX_SLOTS = 4, RIBBONS_PER_ROW = 3;
  const RIBBON_ASPECT = 100 / 400;

  // ── Device Group Normalizer ───────────────────────────
  function normalizeGroup(g) {
    const rawType = (g.input_type || 'quantity').toLowerCase();
    // 'dropdown' is the metaobject label for a mutually-exclusive pick list —
    // treat it identically to 'select_one' everywhere downstream.
    const type = rawType === 'dropdown' ? 'select_one' : rawType;
    const ow = g.overlay_width || 0;
    if (g.options && g.options.length > 0) return { display_name: g.display_name, input_type: type, slots_per_selection: g.slots_per_selection || 1, overlay_width: ow, image: g.image || (g.options[0] && g.options[0].image) || null, options: g.options };
    if (g.qty_products) { const opts = []; for (let q = 1; q <= 4; q++) { const p = g.qty_products[String(q)]; if (p) opts.push(p); else break; } if (opts.length) return { display_name: g.display_name, input_type: type === 'select_one' ? 'quantity' : type, slots_per_selection: 1, overlay_width: ow, image: g.image || null, options: opts }; }
    return null;
  }

  // ── State ─────────────────────────────────────────────
  const state = {
    currentStep: 1, selectedRackSize: null, selectedRack: null, selectedVariant: null,
    selectedRibbons: [], buildId: null, ribbonSearchQuery: '', ribbonPage: 1, configuringRibbonId: null
  };

  // ── Helpers ───────────────────────────────────────────
  function fmt(c) { return '$' + (c / 100).toFixed(2); }
  function genId() { return 'BLD-' + Date.now() + '-' + Math.random().toString(36).substring(2, 6).toUpperCase(); }
  function esc(s) { const d = document.createElement('div'); d.textContent = s; return d.innerHTML; }
  function debounce(fn, ms) { let t; return function (...a) { clearTimeout(t); t = setTimeout(() => fn.apply(this, a), ms); }; }
  function hasGroups(r) { return r.device_groups && r.device_groups.length > 0; }
  function hasAnyDevices(r) { return hasGroups(r) || (r.compatible_devices && r.compatible_devices.length > 0); }
  function findEntry(id) { return state.selectedRibbons.find(e => e.ribbon.id === id); }

  // Knot/clasp devices are one physical device, but they span the usable
  // device area of the ribbon. Treat them as occupying all 4 device slots.
  function effectiveGroupSlots(g, selectedIndex) {
    if (!g) return 1;
    if (isWideDevice(g.display_name)) return MAX_SLOTS;
    if (g.input_type === 'select_one' && selectedIndex !== undefined && selectedIndex !== null && selectedIndex >= 0) {
      const opt = g.options && g.options[selectedIndex];
      if (opt && isWideDevice(opt.title)) return MAX_SLOTS;
    }
    return g.slots_per_selection || 1;
  }

  function getAssemblyTier(n) { const t = config.assembly_fee_tiers; if (!t || !t.length) return null; if (n <= (config.assembly_tier1_max || 6) && t[0]) return t[0]; if (n <= (config.assembly_tier2_max || 13) && t[1]) return t[1]; return t[2] || t[t.length - 1]; }

  // ── Slot counting ─────────────────────────────────────
  function slotsUsed(entry) {
    if (!hasGroups(entry.ribbon)) return 0; let total = 0;
    entry.ribbon.device_groups.forEach((rawG, gi) => { const g = normalizeGroup(rawG); if (!g) return; const sel = entry.groupSelections[gi];
      if (sel === undefined || sel === null) return;
      if (g.input_type === 'quantity' && sel <= 0) return; if (g.input_type === 'select_one' && sel < 0) return; if (g.input_type === 'checkbox' && sel <= 0) return;
      if (g.input_type === 'quantity') total += sel; else if (g.input_type === 'select_one' && sel >= 0) total += effectiveGroupSlots(g, sel); else if (g.input_type === 'checkbox' && sel > 0) total += g.slots_per_selection;
    }); return total;
  }
  function entryDeviceCount(entry) { if (hasGroups(entry.ribbon)) return slotsUsed(entry); return Object.values(entry.deviceQty || {}).reduce((a, q) => a + q, 0); }
  function totalDeviceCount() { return state.selectedRibbons.reduce((s, e) => s + entryDeviceCount(e), 0); }

  // ── Attachment counting (for the device fee) ──────────
  // A multi-count of the SAME device on a ribbon (2/3/4 oak leaf clusters or
  // stars) is mounted in one operation, so it bills as ONE attachment, not N.
  // Rule: one attachment per active device group per ribbon. A ribbon with
  // 3 oak leaves + a star = 2 attachments (two different device types).
  function entryAttachmentCount(entry) {
    if (hasGroups(entry.ribbon)) {
      let n = 0;
      entry.ribbon.device_groups.forEach((rawG, gi) => {
        const g = normalizeGroup(rawG); if (!g) return;
        const sel = entry.groupSelections[gi];
        if (sel === undefined || sel === null) return;
        if (g.input_type === 'quantity' && sel > 0) n += 1;
        else if (g.input_type === 'select_one' && sel >= 0) n += 1;
        else if (g.input_type === 'checkbox' && sel > 0) n += 1;
      });
      return n;
    }
    // legacy compatible_devices: each distinct device with qty > 0 = 1 attachment
    return Object.values(entry.deviceQty || {}).filter(q => q > 0).length;
  }
  function totalAttachmentCount() { return state.selectedRibbons.reduce((s, e) => s + entryAttachmentCount(e), 0); }

  // ── Cart item resolution ──────────────────────────────
  // QUANTITY type: each quantity is its own SKU (single/double/triple/quad),
  //   so add that SKU × 1 — inventory decrements the real product and uses
  //   its real price. Falls back to single × qty if the set SKU isn't defined.
  // SELECT_ONE: add the selected option × 1
  // CHECKBOX: add options[0] × 1
  function resolveCartItem(g, sel) {
    if (sel === undefined || sel === null) return null;
    if (g.input_type === 'quantity') {
      const q = +sel || 0; if (q <= 0 || !g.options[0]) return null;
      const setProduct = g.options[q - 1]; // options[0]=single, [1]=double, [2]=triple, [3]=quad
      if (setProduct) return { variant_id: setProduct.variant_id, quantity: 1, price: setProduct.price, title: setProduct.title, image: setProduct.image };
      // fallback: this device only has a single SKU defined → single × qty
      return { variant_id: g.options[0].variant_id, quantity: q, price: g.options[0].price * q, title: g.options[0].title, image: g.options[0].image };
    }
    if (g.input_type === 'select_one') { if (sel < 0 || !g.options[sel]) return null; const o = g.options[sel]; return { variant_id: o.variant_id, quantity: 1, price: o.price, title: o.title, image: o.image }; }
    if (g.input_type === 'checkbox') { if (!sel || !g.options[0]) return null; return { variant_id: g.options[0].variant_id, quantity: 1, price: g.options[0].price, title: g.options[0].title, image: g.options[0].image }; }
    return null;
  }
  function selectionPrice(g, sel) { const i = resolveCartItem(g, sel); return i ? i.price : 0; }
  function selectionLabel(g, sel) {
    if (sel === undefined || sel === null) return '';
    if (g.input_type === 'quantity') { const q = +sel || 0; if (q <= 0) return ''; return q > 1 ? g.display_name + ' × ' + q : g.display_name; }
    const i = resolveCartItem(g, sel); return i ? (i.title || g.display_name) : '';
  }

  // ── Rack layout detection (from variant title) ────────
  function isSpaced() { if (!state.selectedVariant) return false; const t = state.selectedVariant.title.toLowerCase(); return t.includes('space') || t.includes('1/8'); }
  function isCentered() { if (!state.selectedVariant) return true; return !state.selectedVariant.title.toLowerCase().includes('offset'); }

  // ── Device overlay images for preview ─────────────────
  // Sorted by regulation order: V → C → R → Silver → Bronze
  // regardless of metafield order.
  function devicePrecedence(name) {
    const n = (name || '').toLowerCase();
    if (n.includes('v ') || n === 'v' || n.includes('v device')) return 1;
    if (n.includes('c ') || n === 'c' || n.includes('c device')) return 2;
    if (n.includes('r ') || n === 'r' || n.includes('r device')) return 3;
    if (n.includes('silver')) return 4;
    if (n.includes('bronze')) return 5;
    if (n.includes('gold') || n.includes('star')) return 6;
    return 7; // unknown — render last
  }

  // Default wide-overlay width (% of ribbon) for knot/clasp devices when no
  // explicit overlay_width metafield value comes through.
  var KNOT_DEFAULT_WIDTH = 75;
  function isWideDevice(title) {
    if (!title) return false;
    const t = title.toLowerCase();
    return t.indexOf('knot') !== -1 || t.indexOf('clasp') !== -1;
  }

  // Repeated oak leaves and stars should read as a compact cluster rather
  // than four independent centered icons with visible gaps.
  function isTightClusterDevice(title) {
    if (!title) return false;
    const t = title.toLowerCase();
    return t.indexOf('oak') !== -1 || t.indexOf('leaf') !== -1 || t.indexOf('star') !== -1;
  }

  function getDeviceOverlays(entry) {
    const items = []; // { precedence, src, width }
    if (hasGroups(entry.ribbon)) {
      entry.ribbon.device_groups.forEach((rawG, gi) => {
        const g = normalizeGroup(rawG); if (!g) return;
        const sel = entry.groupSelections[gi];
        const prec = devicePrecedence(g.display_name);
        const groupWidth = g.overlay_width || 0; // explicit metafield value, if it resolves
        if (g.input_type === 'quantity') {
          const q = +sel || 0;
          const img = g.options[0] && g.options[0].image;
          const wideGroup = isWideDevice(g.display_name);
          const w = wideGroup ? Math.max(groupWidth || 0, KNOT_DEFAULT_WIDTH) : groupWidth;
          if (q > 0 && img) { for (let i = 0; i < q; i++) items.push({ precedence: prec + i * 0.01, src: img, width: w, tightCluster: isTightClusterDevice(g.display_name) }); }
        } else {
          const item = resolveCartItem(g, sel);
          // width: explicit group value, else infer from the chosen device's name
          const wideItem = (item && isWideDevice(item.title)) || isWideDevice(g.display_name);
          const w = wideItem ? Math.max(groupWidth || 0, KNOT_DEFAULT_WIDTH) : groupWidth;
          if (item && item.image) items.push({ precedence: prec, src: item.image, width: w, tightCluster: isTightClusterDevice(item.title || g.display_name) });
        }
      });
    } else if (entry.ribbon.compatible_devices) {
      entry.ribbon.compatible_devices.forEach(dev => {
        const qty = entry.deviceQty[dev.id] || 0;
        const prec = devicePrecedence(dev.title);
        const width = isWideDevice(dev.title)
          ? Math.max(dev.overlay_width || 0, KNOT_DEFAULT_WIDTH)
          : (dev.overlay_width || 0);
        for (let i = 0; i < qty; i++) { if (dev.image) items.push({ precedence: prec + i * 0.01, src: dev.image, width: width, tightCluster: isTightClusterDevice(dev.title) }); }
      });
    }
    // Sort by precedence, then return overlay objects
    items.sort((a, b) => a.precedence - b.precedence);
    return items.map(i => ({ src: i.src, width: i.width, tightCluster: !!i.tightCluster }));
  }

  /** Position device overlays.
   *  Small devices (stars, oak leaves, numerals) cluster in the center at a
   *  uniform square size. Wide devices (knot clasps, width > 0) render as a
   *  single image centered at that % of the ribbon width, height auto.
   *  sizePercent is the small-device height as % of slot height. */
  function positionOverlays(slot, overlays, sizePercent) {
    if (!overlays.length) return;
    // normalize: tolerate plain strings (treated as small)
    const list = overlays.map(o => (typeof o === 'string') ? { src: o, width: 0, tightCluster: false } : o);
    const wides = list.filter(o => o.width > 0);
    const smalls = list.filter(o => !(o.width > 0));

    // Wide devices (knot clasps): render the device image centered at
    // overlay_width of the ribbon width, full ribbon height, keeping aspect
    // (contain). This is correct when the source PNG is cropped tightly to the
    // knot bar (transparent margins removed). If the bar floats in a large
    // square canvas it will look small — fix the image, not this code.
    wides.forEach((o, i) => {
      const box = document.createElement('div');
      box.className = 'arb2__preview-device--wide';
      box.style.position = 'absolute';
      box.style.pointerEvents = 'none';
      box.style.width = o.width + '%';
      box.style.height = '100%';
      box.style.left = '50%';
      box.style.top = '50%';
      box.style.transform = 'translate(-50%, -50%)';
      box.style.backgroundImage = 'url("' + o.src + '")';
      box.style.backgroundRepeat = 'no-repeat';
      box.style.backgroundPosition = 'center';
      // "cover" is intentional for the transparent 400x200 knot/clasp PNGs.
      // It fills the 75%-wide overlay area and crops excess transparent canvas,
      // making the visible knot span the expected width on the ribbon.
      box.style.backgroundSize = 'cover';
      box.style.zIndex = String(i + 2);
      slot.appendChild(box);
    });

    if (!smalls.length) return;
    const count = smalls.length;
    // device height as % of slot height — uniform when a number is passed
    const devH = (typeof sizePercent === 'number')
      ? sizePercent
      : (sizePercent[Math.min(count, 4)] || sizePercent[4] || 44);
    // To render a SQUARE device on a 4:1 ribbon slot, devW = devH / 4
    const slotRatio = 400 / 100;
    const devW = devH / slotRatio;

    const allTightCluster = smalls.every(o => o.tightCluster);
    const normalOffsets = { 1: 0, 2: devW * 1.15, 3: devW * 1.05, 4: devW * 0.95 };
    // 0.82/0.80/0.78 creates a slight visual overlap of transparent PNG
    // canvases so oak leaves/stars appear almost touching in the preview.
    const tightOffsets = { 1: 0, 2: devW * 0.82, 3: devW * 0.80, 4: devW * 0.78 };
    const offsets = allTightCluster ? tightOffsets : normalOffsets;
    const offset = offsets[Math.min(count, 4)] || devW * (allTightCluster ? 0.78 : 0.95);
    const clusterWidth = (count - 1) * offset + devW;
    const startLeft = (100 - clusterWidth) / 2;

    smalls.forEach((o, oi) => {
      const img = document.createElement('img');
      img.src = o.src; img.alt = ''; img.loading = 'lazy';
      img.className = 'arb2__preview-device';
      img.style.width = devW + '%';
      img.style.height = devH + '%';
      img.style.top = ((100 - devH) / 2) + '%';
      img.style.left = (startLeft + oi * offset) + '%';
      img.style.zIndex = String(oi + 2);
      slot.appendChild(img);
    });
  }

  // ── Navigation ────────────────────────────────────────
  function goToStep(step) {
    state.currentStep = step;
    document.querySelectorAll('.arb2__progress-step').forEach(el => {
      const s = +el.dataset.step;
      el.classList.toggle('arb2__progress-step--active', s <= step);
      el.classList.toggle('arb2__progress-step--current', s === step);
      if (s === step) el.setAttribute('aria-current', 'step');
      else el.removeAttribute('aria-current');
    });
    document.querySelectorAll('.arb2__step').forEach(el => { el.style.display = +el.dataset.stepPanel === step ? '' : 'none'; });
    document.getElementById('army-ribbon-builder-v2').scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (step === 2) { updateCounter(); renderBrowser(); renderSummary(); renderPreview(); }
    if (step === 3) renderReview();
  }

  // ═══════════════════════════════════════════════════════
  // STEP 1 — Rack Selection (1 product per size, variants)
  // ═══════════════════════════════════════════════════════
  function initStep1() {
    const g = document.getElementById('arb2-rack-size-grid'); g.innerHTML = '';
    if (!availableSizes.length) { g.innerHTML = '<p style="color:#C0392B">No racks found.</p>'; return; }
    availableSizes.forEach(sz => {
      const b = document.createElement('button'); b.className = 'arb2__size-btn'; b.textContent = sz; b.dataset.size = sz;
      b.addEventListener('click', () => {
        state.selectedRackSize = sz; state.selectedRack = racksBySize[sz]; state.selectedVariant = null;
        document.querySelectorAll('.arb2__size-btn').forEach(x => x.classList.toggle('arb2__size-btn--active', +x.dataset.size === sz));
        if (state.selectedRibbons.length > sz) state.selectedRibbons.length = sz;
        document.getElementById('arb2-rack-summary').style.display = 'none';
        document.getElementById('arb2-step1-next').disabled = true;
        renderVariants();
      });
      g.appendChild(b);
    });
  }

  function renderVariants() {
    const rack = state.selectedRack; if (!rack) return;
    const c = document.getElementById('arb2-rack-variants'), g = document.getElementById('arb2-rack-variant-grid');
    g.innerHTML = ''; g.classList.remove('arb2__rack-variant-grid--stacked');
    const vs = rack.variants.filter(v => v.available);
    if (!vs.length) { g.innerHTML = '<p>Out of stock.</p>'; c.style.display = ''; return; }

    // Single variant (e.g., sizes 2-3) — auto-select, skip showing buttons
    if (vs.length === 1) { c.style.display = 'none'; pickVariant(vs[0]); return; }

    // Multiple variants — show compact style buttons
    g.classList.add('arb2__rack-variant-grid--stacked');
    vs.forEach(v => {
      const b = document.createElement('button'); b.className = 'arb2__style-btn'; b.dataset.variantId = v.id;
      b.innerHTML = '<span class="arb2__style-btn-name">' + esc(v.title) + '</span><span class="arb2__style-btn-price">' + fmt(v.price) + '</span>';
      b.addEventListener('click', () => pickVariant(v)); g.appendChild(b);
    });
    c.style.display = '';
  }

  function pickVariant(v) {
    state.selectedVariant = v;
    document.getElementById('arb2-rack-variants').style.display = 'none';
    const rack = state.selectedRack;
    document.getElementById('arb2-rack-summary-img').src = v.image || rack.image || '';
    document.getElementById('arb2-rack-summary-title').textContent = rack.title;
    document.getElementById('arb2-rack-summary-variant').textContent = v.title !== 'Default Title' ? v.title + ' — ' + fmt(v.price) : fmt(v.price);
    document.getElementById('arb2-rack-summary-price').textContent = '';
    document.getElementById('arb2-rack-summary').style.display = '';
    document.getElementById('arb2-step1-next').disabled = false;
  }

  // ═══════════════════════════════════════════════════════
  // STEP 2 — Browse + Inline Config + Preview
  // ═══════════════════════════════════════════════════════
  function getFiltered() { const q = state.ribbonSearchQuery.toLowerCase(); return (!q || q.length < 2) ? ribbonsSorted : ribbonsSorted.filter(r => r.title.toLowerCase().includes(q)); }

  function renderBrowser() {
    const f = getFiltered(), tp = Math.max(1, Math.ceil(f.length / PER_PAGE));
    state.ribbonPage = Math.max(1, Math.min(state.ribbonPage, tp));
    const pg = f.slice((state.ribbonPage - 1) * PER_PAGE, state.ribbonPage * PER_PAGE);
    const max = state.selectedRackSize || 0, atCap = state.selectedRibbons.length >= max;
    const list = document.getElementById('arb2-ribbon-list'); list.innerHTML = '';

    if (!f.length) list.innerHTML = '<div class="arb2__browse-empty">No ribbons match</div>';
    else pg.forEach(rib => {
      const entry = findEntry(rib.id), added = !!entry, dis = atCap && !added;
      const isConfig = state.configuringRibbonId === rib.id && added;
      const devCount = entry ? entryDeviceCount(entry) : 0;
      const dc = hasGroups(rib) ? rib.device_groups.length : (rib.compatible_devices ? rib.compatible_devices.length : 0);

      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'arb2__browse-item' + (dis ? ' arb2__browse-item--disabled' : '') + (added ? ' arb2__browse-item--added' : '') + (isConfig ? ' arb2__browse-item--config' : '');
      row.disabled = dis;
      row.setAttribute('aria-label',
        added
          ? (hasAnyDevices(rib) ? 'Configure ' + rib.title : rib.title + ' selected')
          : 'Add ' + rib.title
      );
      if (isConfig) row.setAttribute('aria-expanded', 'true');
      else if (added && hasAnyDevices(rib)) row.setAttribute('aria-expanded', 'false');
      row.innerHTML = '<img src="' + (rib.image_small || rib.image) + '" alt="" class="arb2__browse-item-img" loading="lazy" />' +
        '<div class="arb2__browse-item-info"><span class="arb2__browse-item-name">' + esc(rib.title) + '</span><span class="arb2__browse-item-meta">Precedence: ' + rib.precedence + (dc ? ' · ' + dc + ' device options' : '') + '</span></div>' +
        (added ? '<span class="arb2__browse-badge arb2__browse-badge--added">' + (devCount > 0 ? devCount + ' dev' : '✓') + '</span>' : '<span class="arb2__browse-badge arb2__browse-badge--add">+ Add</span>');
      row.addEventListener('click', () => handleRibbonClick(rib));
      list.appendChild(row);

      // Inline device config panel
      if (isConfig && entry && hasAnyDevices(rib)) {
        const panel = document.createElement('div'); panel.className = 'arb2__inline-config';
        let devHtml = '';
        if (hasGroups(rib)) {
          // Only use device_groups if at least one group normalizes successfully
          // (has options or qty_products populated). If they all null out (e.g. the
          // Knot Devices metaobject only has a name, no options), fall through to
          // compatible_devices which holds the actual knot products.
          const hasValidGroups = rib.device_groups.some(g => normalizeGroup(g) !== null);
          devHtml = hasValidGroups ? renderGroupsHtml(entry) : renderLegacyHtml(entry);
        } else {
          devHtml = renderLegacyHtml(entry);
        }
        panel.innerHTML = '<div class="arb2__inline-config-inner"><div class="arb2__inline-config-header"><span>Configure Devices</span><button type="button" class="arb2__inline-close" data-close aria-label="Close device configuration for ' + esc(rib.title) + '">&times;</button></div>' + devHtml + '<div class="arb2__inline-footer"><button type="button" class="arb2__inline-remove" data-rid="' + rib.id + '">Remove Ribbon</button><button type="button" class="arb2__inline-done" data-close>Done</button></div></div>';
        list.appendChild(panel);

        panel.querySelectorAll('.arb2__dg-select').forEach(s => s.addEventListener('change', function () { entry.groupSelections[+this.dataset.gi] = +this.value; renderBrowser(); renderSummary(); renderPreview(); }));
        panel.querySelectorAll('.arb2__dg-checkbox').forEach(cb => cb.addEventListener('change', function () { entry.groupSelections[+this.dataset.gi] = this.checked ? 1 : 0; renderBrowser(); renderSummary(); renderPreview(); }));
        panel.querySelectorAll('.arb2__old-select').forEach(s => s.addEventListener('change', function () { if (+this.value <= 0) delete entry.deviceQty[+this.dataset.did]; else entry.deviceQty[+this.dataset.did] = +this.value; renderBrowser(); renderSummary(); renderPreview(); }));
        // Single-select (knot/clasp) panel: mutually exclusive, only one device at a time
        panel.querySelectorAll('[data-did-select]').forEach(s => s.addEventListener('change', function () {
          // Clear all existing selections, then set the chosen one
          entry.ribbon.compatible_devices.forEach(d => delete entry.deviceQty[d.id]);
          if (+this.value > 0) entry.deviceQty[+this.value] = 1;
          renderBrowser(); renderSummary(); renderPreview();
        }));
        panel.querySelectorAll('[data-close]').forEach(b => b.addEventListener('click', (e) => { e.stopPropagation(); state.configuringRibbonId = null; renderBrowser(); }));
        panel.querySelectorAll('.arb2__inline-remove').forEach(b => b.addEventListener('click', (e) => { e.stopPropagation(); removeRibbon(+b.dataset.rid); }));
        setTimeout(() => panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      }
    });

    // Pagination
    const pag = document.getElementById('arb2-ribbon-pagination');
    if (tp <= 1) pag.style.display = 'none'; else {
      pag.style.display = ''; pag.innerHTML = '';
      const mk = (t, p, d) => { const b = document.createElement('button'); b.className = 'arb2__pag-btn' + (p === state.ribbonPage ? ' arb2__pag-btn--active' : ''); b.textContent = t; b.disabled = d; b.addEventListener('click', () => { state.ribbonPage = p; renderBrowser(); }); return b; };
      pag.appendChild(mk('←', state.ribbonPage - 1, state.ribbonPage <= 1));
      for (let i = 1; i <= tp; i++) { if (i === 1 || i === tp || Math.abs(i - state.ribbonPage) <= 1) pag.appendChild(mk(i, i, false)); else if (Math.abs(i - state.ribbonPage) === 2) { const d = document.createElement('span'); d.className = 'arb2__pag-dots'; d.textContent = '…'; pag.appendChild(d); } }
      pag.appendChild(mk('→', state.ribbonPage + 1, state.ribbonPage >= tp));
    }
    document.getElementById('arb2-ribbon-browse-count').textContent = state.ribbonSearchQuery.length >= 2 ? f.length + ' found' : f.length + ' ribbons — by precedence';
  }

  // ── Device Groups HTML ────────────────────────────────
  function renderGroupsHtml(entry) {
    const used = slotsUsed(entry), remaining = MAX_SLOTS - used;
    return '<div class="arb2__device-section"><div class="arb2__device-label">Select Devices <span class="arb2__slot-indicator">' + used + ' / ' + MAX_SLOTS + ' slots</span></div>' +
      entry.ribbon.device_groups.map((rawG, gi) => {
        const g = normalizeGroup(rawG); if (!g) return '';
        const sel = entry.groupSelections[gi];
        if (g.input_type === 'quantity') {
          const cq = sel || 0, maxQ = Math.min(MAX_SLOTS, cq + remaining), ia = cq > 0;
          const setProduct = ia ? g.options[cq - 1] : null;
          const pr = ia ? (setProduct ? fmt(setProduct.price) : fmt((g.options[0] ? g.options[0].price : 0) * cq)) : '';
          let o = '<option value="0"' + (!ia ? ' selected' : '') + '>NONE</option>';
          for (let q = 1; q <= maxQ; q++) o += '<option value="' + q + '"' + (q === cq ? ' selected' : '') + '>' + q + '</option>';
          return '<div class="arb2__dg-row' + (ia ? ' arb2__dg-row--active' : '') + '"><img src="' + (g.image || '') + '" alt="" class="arb2__dg-row-img" /><div class="arb2__dg-row-info"><span class="arb2__dg-row-name">' + esc(g.display_name) + '</span><span class="arb2__dg-row-price">' + pr + '</span></div><select class="arb2__dg-select' + (ia ? ' arb2__dg-select--active' : '') + '" data-gi="' + gi + '" aria-label="' + esc(g.display_name) + ' quantity">' + o + '</select></div>';
        }
        if (g.input_type === 'select_one') {
          const ci = (sel !== undefined && sel >= 0) ? sel : -1, ia = ci >= 0, sn = effectiveGroupSlots(g, ci), cs = ia || remaining >= sn, pr = ia ? fmt(g.options[ci].price) : '';
          let o = '<option value="-1"' + (!ia ? ' selected' : '') + '>NONE</option>';
          g.options.forEach((opt, oi) => {
            let label = opt.title;
            if (isWideDevice(g.display_name) || isWideDevice(opt.title)) {
              label = knotAwardLabel(opt.title, oi);
            } else if ((g.display_name || '').toLowerCase().includes('numeral') || (opt.title || '').toLowerCase().includes('numeral')) {
              label = numeralLabel(opt.title, oi);
            }
            o += '<option value="' + oi + '"' + (oi === ci ? ' selected' : '') + ((!cs && oi !== ci) ? ' disabled' : '') + '>' + esc(label) + ' — ' + fmt(opt.price) + '</option>';
          });
          return '<div class="arb2__dg-row' + (ia ? ' arb2__dg-row--active' : '') + '" data-type="select_one"><img src="' + (g.image || '') + '" alt="" class="arb2__dg-row-img" /><div class="arb2__dg-row-info"><span class="arb2__dg-row-name">' + esc(g.display_name) + '</span><span class="arb2__dg-row-price">' + pr + '</span></div><select class="arb2__dg-select' + (ia ? ' arb2__dg-select--active' : '') + '" data-gi="' + gi + '" aria-label="Choose ' + esc(g.display_name) + '">' + o + '</select></div>';
        }
        if (g.input_type === 'checkbox') {
          const ia = sel > 0, ddis = !ia && remaining < (g.slots_per_selection || 1), pr = g.options[0] ? fmt(g.options[0].price) : '';
          return '<div class="arb2__dg-row' + (ia ? ' arb2__dg-row--active' : '') + '"><img src="' + (g.image || '') + '" alt="" class="arb2__dg-row-img" /><div class="arb2__dg-row-info"><span class="arb2__dg-row-name">' + esc(g.display_name) + '</span><span class="arb2__dg-row-price">' + pr + '</span></div><label class="arb2__dg-cb-wrap"><input type="checkbox" class="arb2__dg-checkbox" data-gi="' + gi + '" ' + (ia ? 'checked' : '') + ' ' + (ddis ? 'disabled' : '') + ' /><span>' + (ia ? 'ADDED' : 'ADD') + '</span></label></div>';
        }
        return '';
      }).join('') + '</div>';
  }

  // Short display label for knot/clasp award choices.
  // Product/cart titles remain unchanged; this only changes the dropdown text.
  function knotAwardLabel(title, index) {
    const t = (title || '').toLowerCase();
    const m = t.match(/(?:^|\s)(\d+)(?:st|nd|rd|th)?\s+award/);
    if (m) {
      const n = parseInt(m[1], 10);
      const mod100 = n % 100, mod10 = n % 10;
      const suffix = (mod100 >= 11 && mod100 <= 13) ? 'th'
        : mod10 === 1 ? 'st'
        : mod10 === 2 ? 'nd'
        : mod10 === 3 ? 'rd' : 'th';
      return n + suffix + ' Award';
    }
    const n = index + 1;
    const mod100 = n % 100, mod10 = n % 10;
    const suffix = (mod100 >= 11 && mod100 <= 13) ? 'th'
      : mod10 === 1 ? 'st'
      : mod10 === 2 ? 'nd'
      : mod10 === 3 ? 'rd' : 'th';
    return n + suffix + ' Award';
  }

  function numeralLabel(title, index) {
    const t = (title || '').toLowerCase();
    const m = t.match(/numeral\s+(\d+)/);
    if (m) return 'Numeral ' + parseInt(m[1], 10);

    const m2 = t.match(/(?:^|\s)(\d+)(?:\s|$)/);
    if (m2) return 'Numeral ' + parseInt(m2[1], 10);

    return 'Numeral ' + (index + 2);
  }


  function renderLegacyHtml(entry) {
    const devs = entry.ribbon.compatible_devices;
    if (!devs || !devs.length) return '';
    const total = Object.values(entry.deviceQty || {}).reduce((s, q) => s + q, 0);
    // If there is exactly one device OR every device has max_qty = 1, treat as a
    // select-one panel: one dropdown listing all the device options (NONE + each device).
    // This handles knot clasps: one group, mutually exclusive choices, centered wide overlay.
    const allSingleSelect = devs.every(d => (d.max_qty || MAX_SLOTS) <= 1);
    if (allSingleSelect) {
      const selectedId = Object.keys(entry.deviceQty).find(id => entry.deviceQty[id] > 0) || null;
      const ia = !!selectedId;
      const selDev = selectedId ? devs.find(d => String(d.id) === String(selectedId)) : null;
      const pr = selDev ? fmt(selDev.price) : '';
      const img = selDev ? selDev.image : (devs[0] ? devs[0].image : '');
      let o = '<option value="0"' + (!ia ? ' selected' : '') + '>NONE</option>';
      devs.forEach((dev, di) => {
        const titleLower = (dev.title || '').toLowerCase();
        const label = wideSelect
          ? knotAwardLabel(dev.title, di)
          : (titleLower.includes('numeral') ? numeralLabel(dev.title, di) : dev.title);
        o += '<option value="' + dev.id + '"' + (String(dev.id) === String(selectedId) ? ' selected' : '') + '>' + esc(label) + ' — ' + fmt(dev.price) + '</option>';
      });
      const wideSelect = devs.some(d => isWideDevice(d.title));
      const slotMax = wideSelect ? MAX_SLOTS : 1;
      const slotUsed = ia ? slotMax : 0;
      return '<div class="arb2__device-section"><div class="arb2__device-label">Select Devices <span class="arb2__slot-indicator">' + slotUsed + ' / ' + slotMax + ' slots</span></div>' +
        '<div class="arb2__dg-row' + (ia ? ' arb2__dg-row--active' : '') + '" data-type="select_one">' +
        '<img src="' + (img || '') + '" alt="" class="arb2__dg-row-img" />' +
        '<div class="arb2__dg-row-info"><span class="arb2__dg-row-name">' + (selDev ? esc(selDev.title) : 'Choose a device') + '</span><span class="arb2__dg-row-price">' + pr + '</span></div>' +
        '<select class="arb2__old-select' + (ia ? ' arb2__dg-select--active' : '') + '" data-did-select="1" aria-label="Choose knot or clasp award">' + o + '</select></div></div>';
    }
    // Normal multi-device: individual NONE/1..N stepper per device
    return '<div class="arb2__device-section"><div class="arb2__device-label">Select Devices' + (total ? ' <span class="arb2__device-count">' + total + '</span>' : '') + '</div>' +
      devs.map(dev => {
        const qty = entry.deviceQty[dev.id] || 0, ia = qty > 0;
        const maxQ = Math.min(MAX_SLOTS, dev.max_qty || MAX_SLOTS);
        let o = '<option value="0"' + (!ia ? ' selected' : '') + '>NONE</option>';
        for (let q = 1; q <= maxQ; q++) o += '<option value="' + q + '"' + (q === qty ? ' selected' : '') + '>' + q + '</option>';
        return '<div class="arb2__dg-row' + (ia ? ' arb2__dg-row--active' : '') + '"><img src="' + (dev.image || '') + '" alt="" class="arb2__dg-row-img" /><div class="arb2__dg-row-info"><span class="arb2__dg-row-name">' + esc(dev.title) + '</span><span class="arb2__dg-row-price">' + (ia ? fmt(dev.price * qty) : fmt(dev.price)) + '</span></div><select class="arb2__old-select' + (ia ? ' arb2__dg-select--active' : '') + '" data-did="' + dev.id + '" aria-label="' + esc(dev.title) + ' quantity">' + o + '</select></div>';
      }).join('') + '</div>';
  }

  function handleRibbonClick(ribbon) {
    const existing = findEntry(ribbon.id);
    if (existing) { state.configuringRibbonId = (state.configuringRibbonId === ribbon.id) ? null : (hasAnyDevices(ribbon) ? ribbon.id : null); renderBrowser(); return; }
    const max = state.selectedRackSize || 0;
    if (state.selectedRibbons.length >= max) return;
    if (state.selectedRibbons.some(e => e.ribbon.id === ribbon.id) && !confirm('"' + ribbon.title + '" already added. Add another?')) return;
    state.selectedRibbons.push({ ribbon, groupSelections: {}, deviceQty: {} });
    state.selectedRibbons.sort((a, b) => a.ribbon.precedence - b.ribbon.precedence);
    state.configuringRibbonId = hasAnyDevices(ribbon) ? ribbon.id : null;
    renderBrowser(); renderSummary(); renderPreview(); updateCounter(); updateNext();
  }

  function removeRibbon(ribbonId) {
    const idx = state.selectedRibbons.findIndex(e => e.ribbon.id === ribbonId);
    if (idx >= 0) state.selectedRibbons.splice(idx, 1);
    if (state.configuringRibbonId === ribbonId) state.configuringRibbonId = null;
    renderBrowser(); renderSummary(); renderPreview(); updateCounter(); updateNext();
  }

  function initSearch() {
    const input = document.getElementById('arb2-ribbon-search');
    input.addEventListener('input', debounce(function () { state.ribbonSearchQuery = this.value.trim(); state.ribbonPage = 1; state.configuringRibbonId = null; renderBrowser(); }, 200));
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); state.ribbonSearchQuery = this.value.trim(); state.ribbonPage = 1; state.configuringRibbonId = null; renderBrowser(); this.blur(); } });
  }

  function updateCounter() { const el = document.getElementById('arb2-ribbon-counter'), m = state.selectedRackSize || 0, c = state.selectedRibbons.length; el.textContent = c + ' / ' + m + ' ribbons'; el.classList.toggle('arb2__counter--full', c >= m); }
  function updateNext() { document.getElementById('arb2-step2-next').disabled = !state.selectedRibbons.length; }

  // ═══════════════════════════════════════════════════════
  // LIVE VISUAL RACK PREVIEW — OFFICIAL RACK LAYOUTS
  // ═══════════════════════════════════════════════════════
  // Layout arrays are TOP -> BOTTOM because the preview DOM renders that way.
  // Each row is [ribbonCount, offsetSlots].
  //
  // Centered racks are generated automatically in standard rows of 3.
  // Offset racks use the explicit physical rack configurations below.
  // Sizes 2–7 are centered only.
  //
  // Offset positioning:
  //   [3, 0]   = full row
  //   [2, 1]   = two-ribbon offset row
  //   [1, 1.5] = one ribbon centered over the offset ribbons below
  const ARB2_OFFSET_LAYOUTS = {
    8:  [[2,1],[3,0],[3,0]],
    9:  [[1,1.5],[2,1],[3,0],[3,0]],
    10: [[2,1],[2,1],[3,0],[3,0]],
    11: [[1,1.5],[2,1],[2,1],[3,0],[3,0]],
    12: [[1,1.5],[2,1],[3,0],[3,0],[3,0]],
    13: [[2,1],[2,1],[3,0],[3,0],[3,0]],
    14: [[1,1.5],[2,1],[2,1],[3,0],[3,0],[3,0]],
    15: [[1,1.5],[2,1],[3,0],[3,0],[3,0],[3,0]],
    16: [[2,1],[2,1],[3,0],[3,0],[3,0],[3,0]],
    17: [[1,1.5],[2,1],[2,1],[3,0],[3,0],[3,0],[3,0]],
    18: [[2,1],[2,1],[2,1],[3,0],[3,0],[3,0],[3,0]],
    19: [[2,1],[2,1],[3,0],[3,0],[3,0],[3,0],[3,0]],
    20: [[1,1.5],[2,1],[2,1],[3,0],[3,0],[3,0],[3,0],[3,0]],
    21: [[2,1],[2,1],[2,1],[3,0],[3,0],[3,0],[3,0],[3,0]],
    22: [[1,1.5],[2,1],[2,1],[2,1],[3,0],[3,0],[3,0],[3,0],[3,0]],
    23: [[1,1.5],[2,1],[2,1],[3,0],[3,0],[3,0],[3,0],[3,0],[3,0]],
    24: [[2,1],[2,1],[2,1],[3,0],[3,0],[3,0],[3,0],[3,0],[3,0]],
    25: [[1,1.5],[2,1],[2,1],[2,1],[3,0],[3,0],[3,0],[3,0],[3,0],[3,0]]
  };

  // Retained as a fallback for any future one-off custom.row_layout metafield.
  function parseLayout(str) {
    if (!str || typeof str !== 'string') return null;
    const rows = [];
    for (const tok of str.split(',')) {
      const t = tok.trim();
      if (!t) continue;
      const parts = t.split(':');
      const count = parseInt(parts[0], 10);
      const off = parts.length > 1 ? parseFloat(parts[1]) : 0;
      if (!(count > 0) || isNaN(off)) return null;
      rows.push([count, off]);
    }
    return rows.length ? rows : null;
  }

  function getRackPreviewLayout(totalSlots) {
    // 2–7 are centered only. All centered variants use the normal
    // auto-generated centered layout regardless of rack size.
    if (isCentered() || totalSlots <= 7) return null;

    // Official physical offset rack configuration takes priority.
    if (ARB2_OFFSET_LAYOUTS[totalSlots]) return ARB2_OFFSET_LAYOUTS[totalSlots];

    // Future one-off fallback.
    const customLayout = (state.selectedVariant && state.selectedVariant.layout)
      ? parseLayout(state.selectedVariant.layout)
      : null;

    if (customLayout) {
      const sum = customLayout.reduce((s, row) => s + row[0], 0);
      if (sum === totalSlots) return customLayout;
      console.warn('ARB2: custom row_layout sums to ' + sum + ' but rack_size is ' + totalSlots + ' — using centered fallback');
    }

    return null;
  }

  function buildPreviewSlot(ribbons, idx) {
    const slot = document.createElement('div');
    slot.className = 'arb2__preview-slot';

    if (idx < ribbons.length) {
      const entry = ribbons[idx];
      slot.classList.add('arb2__preview-slot--filled');

      const img = document.createElement('img');
      img.src = entry.ribbon.image;
      img.alt = entry.ribbon.title;
      img.className = 'arb2__preview-ribbon-img';
      img.loading = 'lazy';
      slot.appendChild(img);

      positionOverlays(slot, getDeviceOverlays(entry), 75);
      slot.title = entry.ribbon.title;
    } else {
      slot.classList.add('arb2__preview-slot--empty');
    }

    return slot;
  }

  function renderPreview() {
    const frame = document.getElementById('arb2-preview-frame');
    const emptyMsg = document.getElementById('arb2-preview-empty');
    frame.innerHTML = '';

    const ribbons = state.selectedRibbons;
    const totalSlots = state.selectedRackSize || 0;
    const spaced = isSpaced();

    // Spacing remains independent from rack geometry.
    frame.classList.toggle('arb2__preview-frame--spaced', spaced);
    frame.classList.toggle('arb2__preview-frame--tight', !spaced);

    if (!ribbons.length) {
      emptyMsg.style.display = '';
      frame.classList.remove('arb2__preview-frame--pattern');
      return;
    }

    emptyMsg.style.display = 'none';

    const layout = getRackPreviewLayout(totalSlots);
    frame.classList.toggle('arb2__preview-frame--pattern', !!layout);

    // OFFSET RACK — use the official physical row map.
    if (layout) {
      let ribbonIdx = 0;

      layout.forEach(([count, off]) => {
        const rowEl = document.createElement('div');
        rowEl.className = 'arb2__preview-row';
        rowEl.style.setProperty('--row-offset', String(off));

        for (let col = 0; col < count; col++) {
          rowEl.appendChild(buildPreviewSlot(ribbons, ribbonIdx));
          ribbonIdx++;
        }

        frame.appendChild(rowEl);
      });

      return;
    }

    // CENTERED RACK — standard rows of 3 with an incomplete TOP row centered.
    // Examples:
    // 5  = 2 / 3
    // 7  = 1 / 3 / 3
    // 8  = 2 / 3 / 3
    // 10 = 1 / 3 / 3 / 3
    const totalRows = Math.ceil(totalSlots / RIBBONS_PER_ROW);
    const topRowCount = totalSlots - (totalRows - 1) * RIBBONS_PER_ROW;

    let ribbonIdx = 0;

    for (let row = 0; row < totalRows; row++) {
      const slotsInRow = row === 0 ? topRowCount : RIBBONS_PER_ROW;
      const rowEl = document.createElement('div');
      rowEl.className = 'arb2__preview-row';

      for (let col = 0; col < slotsInRow; col++) {
        rowEl.appendChild(buildPreviewSlot(ribbons, ribbonIdx));
        ribbonIdx++;
      }

      frame.appendChild(rowEl);
    }
  }

  // ── Summary ───────────────────────────────────────────
// ── Summary ───────────────────────────────────────────
function renderSummary() {
  const container = document.getElementById('arb2-selected');
  const empty = document.getElementById('arb2-empty-state');
  const hdr = document.getElementById('arb2-selected-header');

  container.querySelectorAll('.arb2__summary-item').forEach(el => el.remove());

  if (!state.selectedRibbons.length) {
    if (empty) empty.style.display = '';
    if (hdr) hdr.style.display = 'none';
    return;
  }

  if (empty) empty.style.display = 'none';
  if (hdr) hdr.style.display = '';

  state.selectedRibbons.forEach((entry, idx) => {
    const r = entry.ribbon;
    const dc = entryDeviceCount(entry);

    const el = document.createElement('div');
    el.className = 'arb2__summary-item';

    el.innerHTML =
      '<span class="arb2__summary-pos">#' + (idx + 1) + '</span>' +
      '<img src="' + (r.image_small || r.image) + '" alt="" class="arb2__summary-img" />' +
      '<span class="arb2__summary-name">' + esc(r.title) + '</span>' +

      (dc > 0
        ? '<span class="arb2__summary-devs">' +
            dc + ' ' + (dc === 1 ? 'device' : 'devices') +
          '</span>'
        : ''
      ) +

      '<button type="button" class="arb2__summary-remove" ' +
        'data-rid="' + r.id + '" ' +
        'aria-label="Remove ' + esc(r.title) + '">' +
        '&times;' +
      '</button>';

    container.appendChild(el);
  });

  container.querySelectorAll('.arb2__summary-remove').forEach(b =>
    b.addEventListener('click', () => removeRibbon(+b.dataset.rid))
  );
}

  // ═══════════════════════════════════════════════════════
  // STEP 3 — Review
  // ═══════════════════════════════════════════════════════
  function renderReview() {
    const rp = document.getElementById('arb2-review-preview');
    rp.innerHTML = ''; rp.appendChild(document.getElementById('arb2-preview-frame').cloneNode(true));
    const vTitle = state.selectedVariant.title !== 'Default Title' ? state.selectedRack.title + ' — ' + state.selectedVariant.title : state.selectedRack.title;
    document.getElementById('arb2-review-rack').innerHTML = '<div class="arb2__review-line"><span>' + esc(vTitle) + '</span><span>' + fmt(state.selectedVariant.price) + '</span></div>';
    let html = '';
    state.selectedRibbons.forEach((entry, idx) => {
      html += '<div class="arb2__review-line"><span>#' + (idx + 1) + ' — ' + esc(entry.ribbon.title) + '</span><span>' + fmt(entry.ribbon.price) + '</span></div>';
      if (hasGroups(entry.ribbon)) { entry.ribbon.device_groups.forEach((rawG, gi) => { const g = normalizeGroup(rawG); if (!g) return; const item = resolveCartItem(g, entry.groupSelections[gi]); if (item) html += '<div class="arb2__review-line arb2__review-line--indent"><span>↳ ' + esc(selectionLabel(g, entry.groupSelections[gi])) + '</span><span>' + fmt(item.price) + '</span></div>'; }); }
      else if (entry.ribbon.compatible_devices) { entry.ribbon.compatible_devices.forEach(dev => { const q = entry.deviceQty[dev.id] || 0; if (q > 0) html += '<div class="arb2__review-line arb2__review-line--indent"><span>↳ ' + esc(dev.title) + (q > 1 ? ' × ' + q : '') + '</span><span>' + fmt(dev.price * q) + '</span></div>'; }); }
    });
    document.getElementById('arb2-review-ribbons').innerHTML = html;
    const td = totalDeviceCount(), ac = totalAttachmentCount(), rc = state.selectedRibbons.length, tier = getAssemblyTier(rc);
    let fh = '';
    if (tier) fh += '<div class="arb2__review-line"><span>Assembly — ' + esc(tier.title) + '</span><span>' + fmt(tier.price) + '</span></div>';
    if (config.device_fee_variant_id && ac > 0) fh += '<div class="arb2__review-line"><span>Device Attachment × ' + ac + '</span><span>' + fmt(config.device_fee_price * ac) + '</span></div>';
    document.getElementById('arb2-review-fees').innerHTML = fh || '<div class="arb2__review-line"><span>No additional fees</span><span>—</span></div>';
    document.getElementById('arb2-review-total').textContent = fmt(calcTotal());
  }

  function calcTotal() {
    let t = state.selectedVariant ? state.selectedVariant.price : 0;
    state.selectedRibbons.forEach(entry => { t += entry.ribbon.price;
      if (hasGroups(entry.ribbon)) { entry.ribbon.device_groups.forEach((rawG, gi) => { const g = normalizeGroup(rawG); if (g) t += selectionPrice(g, entry.groupSelections[gi]); }); }
      else if (entry.ribbon.compatible_devices) { entry.ribbon.compatible_devices.forEach(dev => { const q = entry.deviceQty[dev.id] || 0; if (q > 0) t += dev.price * q; }); }
    });
    const tier = getAssemblyTier(state.selectedRibbons.length); if (tier) t += tier.price;
    const ac = totalAttachmentCount(); if (config.device_fee_variant_id && ac > 0) t += config.device_fee_price * ac;
    return t;
  }

  // ═══════════════════════════════════════════════════════
  // Add to Cart
  // ═══════════════════════════════════════════════════════
  async function addToCart() {
    const btn = document.getElementById('arb2-add-to-cart'); btn.disabled = true; btn.textContent = 'Adding to cart...';
    state.buildId = genId();
    const rc = state.selectedRibbons.length, td = totalDeviceCount(), ac = totalAttachmentCount(), tier = getAssemblyTier(rc);
    const summary = state.selectedRibbons.map((entry, idx) => {
      let line = (idx + 1) + '. ' + entry.ribbon.title; const devs = [];
      if (hasGroups(entry.ribbon)) { entry.ribbon.device_groups.forEach((rawG, gi) => { const g = normalizeGroup(rawG); if (!g) return; const item = resolveCartItem(g, entry.groupSelections[gi]); if (item) devs.push(selectionLabel(g, entry.groupSelections[gi])); }); }
      else if (entry.ribbon.compatible_devices) { entry.ribbon.compatible_devices.forEach(dev => { const q = entry.deviceQty[dev.id] || 0; if (q > 0) devs.push(q > 1 ? dev.title + ' x' + q : dev.title); }); }
      if (devs.length) line += ' [' + devs.join(', ') + ']'; return line;
    });
    const bundleTitle = (config.bundle_label || 'Custom Ribbon Rack') + ' — ' + rc + (rc === 1 ? ' Ribbon' : ' Ribbons') +
      (state.selectedRack ? ' (' + state.selectedRack.title + ')' : '');
    const items = [];
    const vTitle = state.selectedVariant.title !== 'Default Title' ? state.selectedRack.title + ' — ' + state.selectedVariant.title : state.selectedRack.title;
    items.push({ id: state.selectedVariant.id, quantity: 1, properties: { '_Build ID': state.buildId, '_Build Type': 'Ribbon Rack', 'Assembly': 'Yes', 'Configuration': rc + ' Ribbons — ' + vTitle, 'Build Summary': summary.join(' | ') } });
    state.selectedRibbons.forEach((entry, idx) => {
      const devStrs = [];
      if (hasGroups(entry.ribbon)) {
        entry.ribbon.device_groups.forEach((rawG, gi) => { const g = normalizeGroup(rawG); if (!g) return; const item = resolveCartItem(g, entry.groupSelections[gi]); if (item) { devStrs.push(selectionLabel(g, entry.groupSelections[gi])); items.push({ id: item.variant_id, quantity: item.quantity, properties: { '_Build ID': state.buildId, 'For Ribbon': '#' + (idx + 1) + ' — ' + entry.ribbon.title, 'Device': g.display_name, 'Selection': selectionLabel(g, entry.groupSelections[gi]) } }); } });
      } else if (entry.ribbon.compatible_devices) {
        entry.ribbon.compatible_devices.forEach(dev => { const q = entry.deviceQty[dev.id] || 0; if (q > 0) { devStrs.push(q > 1 ? dev.title + ' x' + q : dev.title); items.push({ id: dev.variant_id, quantity: q, properties: { '_Build ID': state.buildId, 'For Ribbon': '#' + (idx + 1) + ' — ' + entry.ribbon.title, 'Quantity on Ribbon': String(q) } }); } });
      }
      const props = { '_Build ID': state.buildId, 'Rack Position': '#' + (idx + 1) + ' of ' + rc };
      if (devStrs.length) props['Devices'] = devStrs.join(', ');
      items.push({ id: entry.ribbon.variant_id, quantity: 1, properties: props });
    });
    if (tier) items.push({ id: tier.variant_id, quantity: 1, properties: { '_Build ID': state.buildId, '_fee_item': 'true', '_Tier': tier.title } });
    if (config.device_fee_variant_id && ac > 0) items.push({ id: config.device_fee_variant_id, quantity: ac, properties: { '_Build ID': state.buildId, '_fee_item': 'true', '_Service': 'Device attachment' } });
    // Stamp every line with the same _Build ID + _Bundle Title so the cart can
    // collapse the build into one clean line (uab-cart-clean.js or the function).
    items.forEach(it => {
      it.properties = it.properties || {};
      it.properties['_Build ID'] = state.buildId;
      it.properties['_Bundle Title'] = bundleTitle;
    });

    try {
      const resp = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      if (!resp.ok) { const e = await resp.json(); throw new Error(e.description || e.message || 'Failed'); }
      document.getElementById('arb2-cart-feedback').style.display = ''; btn.textContent = 'Added to Cart ✓';
      try { const c = await (await fetch('/cart.js')).json(); document.querySelectorAll('.cart-count, .cart-count-bubble, [data-cart-count]').forEach(el => { el.textContent = c.item_count; }); } catch (_) {}
    } catch (err) { alert('Error: ' + err.message); btn.disabled = false; btn.textContent = 'Add Complete Rack to Cart'; }
  }

  function init() {
    initStep1(); initSearch();
    document.getElementById('arb2-step1-next').addEventListener('click', () => goToStep(2));
    document.getElementById('arb2-step2-back').addEventListener('click', () => goToStep(1));
    document.getElementById('arb2-step2-next').addEventListener('click', () => goToStep(3));
    document.getElementById('arb2-step3-back').addEventListener('click', () => goToStep(2));
    document.getElementById('arb2-add-to-cart').addEventListener('click', addToCart);
    document.getElementById('arb2-rack-change').addEventListener('click', () => { document.getElementById('arb2-rack-summary').style.display = 'none'; renderVariants(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();